import React from 'react';

/**
 * Standard props passed by formulaire-dynamique to every custom step (customComponent).
 * Custom steps can destructure the subset they need.
 *
 * @typedef {Object} CustomStepProps
 * @property {number} currentStep - Current step index (0-based)
 * @property {number} totalSteps - Total number of steps
 * @property {function(): void} goToNextStep - Go to next step
 * @property {function(): void} goToPreviousStep - Go to previous step
 * @property {function(number): void} goToStep - Go to a specific step by index
 * @property {function(): void} [resetStepperAndGoToFirstStep] - Reset stepper and go to first step
 * @property {Object} formData - Current form data
 * @property {function(string, *): void} onChange - Update a field (fieldId, value)
 * @property {function(Object): void} setFormData - Replace entire form data
 * @property {function(fieldsToValidate?: any, validateAllSteps?: boolean): { isValid: boolean, errors: Object }} validate - Validate current step or given fields
 * @property {string} language - 'fr' | 'en'
 * @property {Object} [match] - React Router match
 */

/**
 * Returns common navigation labels for custom steps (Previous / Next / Skip).
 *
 * @param {string} language - 'fr' | 'en'
 * @returns {{ precedent: string, suivant: string, skip: string }}
 */
export function getStepNavigationLabels(language = 'fr') {
  const isFr = language === 'fr';
  return {
    precedent: isFr ? 'Précédent' : 'Previous',
    suivant: isFr ? 'Suivant' : 'Next',
    skip: isFr ? 'Passer' : 'Skip',
  };
}

/**
 * Reusable Previous/Next block for custom steps. Renders a single actions div
 * with optional Previous (when showPrevious) and Next (when showNext) buttons.
 * Extra content (e.g. Skip button) can be passed as children between Previous and Next.
 *
 * @param {Object} props
 * @param {number} props.currentStep - Current step index
 * @param {number} props.totalSteps - Total number of steps
 * @param {function(): void} props.onPrevious - Handler for Previous
 * @param {function(): void} props.onNext - Handler for Next
 * @param {{ precedent: string, suivant: string }} props.labels - precedent / suivant labels
 * @param {boolean} [props.showPrevious=true] - Show Previous when currentStep > 0 (default true)
 * @param {boolean} [props.showNext=true] - Show Next when currentStep < totalSteps - 1 (default true)
 * @param {boolean} [props.nextAlwaysVisible=false] - If true, show Next whenever showNext is true (ignore last-step check)
 * @param {boolean} [props.previousDisabled] - Disable Previous button
 * @param {boolean} [props.nextDisabled] - Disable Next button
 * @param {string} [props.actionsClassName] - Class for the actions container
 * @param {string} [props.previousClassName] - Class for the Previous button
 * @param {string} [props.nextClassName] - Class for the Next button
 * @param {React.ReactNode} [props.children] - Extra content (e.g. buttons) between Previous and Next
 */
export function StepNavigation({
  currentStep,
  totalSteps,
  onPrevious,
  onNext,
  labels,
  showPrevious = true,
  showNext = true,
  nextAlwaysVisible = false,
  previousDisabled = false,
  nextDisabled = false,
  actionsClassName = 'formulaire-dynamique-step-actions',
  previousClassName = 'formulaire-dynamique-step-previous-button',
  nextClassName = 'formulaire-dynamique-step-next-button',
  children,
}) {
  const showPrev = showPrevious && currentStep > 0;
  const showNextBtn = showNext && (nextAlwaysVisible || currentStep < totalSteps - 1);

  return (
    <div className={actionsClassName}>
      {showPrev && (
        <button
          type="button"
          className={previousClassName}
          onClick={onPrevious}
          disabled={previousDisabled}
        >
          {labels.precedent}
        </button>
      )}
      {children}
      {showNextBtn && (
        <button
          type="button"
          className={nextClassName}
          onClick={onNext}
          disabled={nextDisabled}
        >
          {labels.suivant}
        </button>
      )}
    </div>
  );
}
